import { Component, computed, OnDestroy, OnInit, Signal, signal, WritableSignal, inject } from '@angular/core';
import { TermsApiService } from '../../../_services/terms-service/terms-api.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HeaderNavComponent } from '../../../shared/components/header-nav/header-nav.component';
import { ProfileDropdownComponent } from '../../../shared/components/profile-dropdown/profile-dropdown.component';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { filter, Subscription } from 'rxjs';
import { TermsContent, TermsStatus } from '../../../_interfaces/terms.interface';
import {
  StatusBadgeComponent,
  statusBadgeDepartments,
} from '../../../shared/components/status-badge/status-badge.component';
import { AccountingProviderCompany } from '../../../_interfaces/accounting.interface';
import { ToastService } from '../../../_services/toast-service/toast.service';
import { HttpErrorResponse } from '@angular/common/http';
import { PageNotFoundComponent } from '../../../shared/components/error-pages/page-not-found/page-not-found.component';

@Component({
  selector: 'app-documents-page',
  imports: [
    HeaderNavComponent,
    ProfileDropdownComponent,
    NgClass,
    LoaderComponent,
    StatusBadgeComponent,
    NgOptimizedImage,
    PageNotFoundComponent,
  ],
  templateUrl: './documents-page.component.html',
})
export class DocumentsPageComponent implements OnInit, OnDestroy {
  readonly termsApiService = inject(TermsApiService);
  readonly sanitizer = inject(DomSanitizer);
  readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  readonly toastService = inject(ToastService);
  readonly subscriptions: Subscription = new Subscription();

  readonly statusBadgeDepartments = statusBadgeDepartments;
  readonly AccountingProviderCompany = AccountingProviderCompany;

  protected termsSlug = '/' + this.termsApiService.TERMS_SLUG;
  protected prepaidTermsSlug = '/' + this.termsApiService.PREPAID_TERMS_SLUG;
  protected privacySlug = '/' + this.termsApiService.PRIVACY_SLUG;

  html = signal<SafeHtml>('');
  date = signal<string>('');
  status = signal<TermsStatus | null>(null);
  isLoading = signal(true);
  is404 = signal(false);
  isTerms: WritableSignal<boolean> = signal<boolean>(false);
  isPrePaidTerms: WritableSignal<boolean> = signal<boolean>(false);
  isPrivacy: WritableSignal<boolean> = signal<boolean>(false);
  isPreview: WritableSignal<boolean> = signal<boolean>(false);

  isMobileView: Signal<boolean> = computed(() => {
    const queryParams = this.route.snapshot.queryParams;
    return queryParams['mobile'];
  });

  protected previewId: Signal<string> = computed(() => {
    const queryParams = this.route.snapshot.queryParams;
    return queryParams['id'];
  });

  constructor() {
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this.isTerms.set(Boolean(event.url.includes(this.termsSlug)));
          this.isPrePaidTerms.set(Boolean(event.url.includes(this.prepaidTermsSlug)));
          this.isPrivacy.set(Boolean(event.url.includes(this.privacySlug)));
          this.isPreview.set(Boolean(event.url.includes('document-preview')));
        }),
    );
  }

  ngOnInit(): void {
    if (this.isTerms()) {
      this.termsApiService.getTermsDocument().subscribe({
        next: (data) => {
          this.handleNext(data);
        },
        error: (err: HttpErrorResponse) => {
          this.handleError(err);
        },
      });
    } else if (this.isPrePaidTerms()) {
      this.termsApiService.getPrepaidCardTermsDocument().subscribe({
        next: (data) => {
          this.handleNext(data);
        },
        error: (err: HttpErrorResponse) => {
          this.handleError(err);
        },
      });
    } else if (this.isPrivacy()) {
      this.termsApiService.getPrivacyDocument().subscribe({
        next: (data) => {
          this.handleNext(data);
        },
        error: (err: HttpErrorResponse) => {
          this.handleError(err);
        },
      });
    } else if (this.isPreview()) {
      this.termsApiService.getDocumentPreview(this.previewId()).subscribe({
        next: (data) => {
          this.handleNext(data);
          this.status.set(data.status);
        },
        error: (err: HttpErrorResponse) => {
          this.handleError(err);
        },
      });
    }
  }

  handleNext(data: TermsContent) {
    this.date.set(data.version);
    this.html.set(this.sanitizer.bypassSecurityTrustHtml(data.content)); //NOSONAR: HTML content is sanitized server-side before being stored in the database
    this.isLoading.set(false);
  }

  handleError(error: HttpErrorResponse) {
    if (error.status === 404) {
      this.is404.set(true);
      this.isLoading.set(false);
    } else {
      this.toastService.show('An error occurred while fetching the page. Please try again later.', 'iconoir-xmark');
    }
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
